import React, { useEffect, useMemo, useState } from "react";
import { Download, HelpCircle, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { QuotePreview } from "../QuotePreview";
import { ESTIMATOR_DEFAULT, LOI_PHASES } from "../../mock";
import { cloneBim3DPricingRules } from "../../lib/bim3dPricingDefaults";
import { getApiBase } from "../../lib/apiBase";

const STORAGE_KEY = "bimcubes_estimator_3d_page";
const infraSet = ["VRD", "Paysagisme", "Paysagisme Electricité"];
const buildingDisciplines = [
  "Architecture",
  "Façades Speciales",
  "Agencement",
  "Structure (Coffrage)",
  "Structure (Ferraillage)",
  "Electricité",
  "Electricité Avancé (Data Centers etc...)",
  "CVC",
  "Plomberie",
];
const getUnitRate = (jh, rateTiers) => {
  const value = Number(jh) || 0;
  for (const tier of rateTiers) {
    if (value >= tier.min) return tier.rate;
  }
  return 280;
};

export const Bim3DEstimatorSection = () => {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const obj = saved ? JSON.parse(saved) : ESTIMATOR_DEFAULT;
    const oldLabels = ["Architecture Façade Avancée", "Architecture Façade avancée"];
    if (obj && Array.isArray(obj.discipline)) {
      obj.discipline = obj.discipline.map((d) => (oldLabels.includes(d) ? "Façades Speciales" : d));
    }
    return obj;
  });
  const [openPreview, setOpenPreview] = useState(false);
  const [pricingRules, setPricingRules] = useState(() => cloneBim3DPricingRules());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    let active = true;
    const API = getApiBase();

    fetch(`${API}/api/bim-3d/pricing-rules`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setPricingRules(data);
      })
      .catch(() => {
        if (active) setPricingRules(cloneBim3DPricingRules());
      });

    return () => {
      active = false;
    };
  }, []);

  const loiFactor = useMemo(() => {
    const phase = LOI_PHASES.find((p) => p.label === state.loiPhase);
    return pricingRules.loiFactors?.[phase?.loi || ""] || 1.0;
  }, [state.loiPhase, pricingRules]);

  const lodFactor = pricingRules.lodFactors?.[String(state.lod)] || 1.0;

  const buildingDisciplineFactor = useMemo(() => {
    const selected = (state.discipline || []).filter((d) => !infraSet.includes(d));
    let sum = 0;
    const contrib = {};
    for (const discipline of selected) {
      const value = pricingRules.disciplineCoefficients?.[discipline] || 0;
      sum += value;
      contrib[discipline] = value;
    }
    return { sum, contrib };
  }, [state.discipline, pricingRules]);

  const daysModeling3D = useMemo(() => {
    const baseJHPerM2 = pricingRules.generalSettings?.baseJhPerM2 ?? 0.0125;
    const area = Math.max(pricingRules.generalSettings?.minimumSurface ?? 500, Number(state.area) || 0);
    const core = area * baseJHPerM2 * lodFactor * loiFactor * (buildingDisciplineFactor.sum || 0);
    const shared = state.specialSurfaces?.vrd || 0;
    const specials =
      ((state.discipline || []).includes("VRD") ? shared * baseJHPerM2 * lodFactor * loiFactor * (pricingRules.infrastructureCoefficients?.["VRD"] ?? 0.1) : 0) +
      ((state.discipline || []).includes("Paysagisme") ? shared * baseJHPerM2 * lodFactor * loiFactor * (pricingRules.infrastructureCoefficients?.["Paysagisme"] ?? 0.1) : 0) +
      ((state.discipline || []).includes("Paysagisme Electricité") ? shared * baseJHPerM2 * lodFactor * loiFactor * (pricingRules.infrastructureCoefficients?.["Paysagisme Electricité"] ?? 0.1) : 0);
    return Math.max(0, core + specials);
  }, [state, buildingDisciplineFactor, lodFactor, loiFactor, pricingRules]);

  const days = useMemo(() => {
    const synthesisDays = state.synthesis ? daysModeling3D : 0;
    const plansDays = (state.plans || 0) * (pricingRules.generalSettings?.daysPerPlan ?? 0.4);
    const urgencyFactor = state.urgency === "Urgent" ? (pricingRules.generalSettings?.urgencyMultiplier ?? 1.3) : 1.0;
    const total = (daysModeling3D + synthesisDays + plansDays) * urgencyFactor;
    return Math.max(0, Math.round(total * 1000) / 1000);
  }, [daysModeling3D, state.synthesis, state.plans, state.urgency, pricingRules]);

  const unitRate = useMemo(() => getUnitRate(days, pricingRules.rateTiers || []), [days, pricingRules]);
  const price = useMemo(() => Math.round(days * unitRate), [days, unitRate]);

  const calcDetail = useMemo(() => {
    const areaInput = Number(state.area) || 0;
    return {
      mode: "3D",
      modeling: {
        area_input_m2: areaInput,
        area_used_m2: Math.max(pricingRules.generalSettings?.minimumSurface ?? 500, areaInput),
        base_jh_per_m2: pricingRules.generalSettings?.baseJhPerM2 ?? 0.0125,
        lod_factor: lodFactor,
        loi_factor: loiFactor,
        building_factor_sum: buildingDisciplineFactor.sum || 0,
        building_contributions: buildingDisciplineFactor.contrib || {},
        specials_shared_m2: state.specialSurfaces?.vrd || 0,
        infrastructure_coefficients: pricingRules.infrastructureCoefficients,
      },
      synthesis: state.synthesis ? { equals_modeling: true } : { enabled: false },
      plans_count: state.plans || 0,
      plans_unit_jh: pricingRules.generalSettings?.daysPerPlan ?? 0.4,
      total_days: days,
      unit_rate_applied: unitRate,
    };
  }, [state, lodFactor, loiFactor, buildingDisciplineFactor, days, unitRate, pricingRules]);

  const toggleDiscipline = (discipline) => {
    const active = state.discipline.includes(discipline);
    const next = active ? state.discipline.filter((x) => x !== discipline) : [...state.discipline, discipline];
    setState({ ...state, discipline: next });
  };

  return (
    <TooltipProvider>
      <section className="px-4 pb-16 pt-8 sm:px-6 lg:px-8 lg:pb-24" data-testid="bim3d-calculator-section" id="calculateur">
        <div className="mx-auto max-w-[1520px] rounded-[2.9rem] border border-[#d9e7d5]/85 bg-[linear-gradient(180deg,_rgba(255,255,255,0.78)_0%,_rgba(248,252,243,0.98)_100%)] px-8 py-10 shadow-[0_36px_96px_-58px_rgba(15,118,110,0.24)] sm:px-11 sm:py-12 lg:px-14 lg:py-14">
          <div className="max-w-4xl">
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="bim3d-calculator-eyebrow">
              Calculateur BIM 3D
            </div>
            <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="bim3d-calculator-title">
              Estimer un projet BIM 3D
            </h2>
            <p className="mt-5 text-lg leading-9 text-slate-600" data-testid="bim3d-calculator-subtitle">
              Renseignez les paramètres principaux de votre projet pour obtenir une première estimation indicative en jours-homme et en montant HT.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1.25fr_0.75fr]">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/90 shadow-[0_28px_76px_-50px_rgba(15,118,110,0.2)]" data-testid="bim3d-calculator-form-card">
              <CardHeader>
                <CardTitle className="text-slate-900">Paramètres du projet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Label htmlFor="bim3d-area-input">Surface Brut Type (m²)</Label>
                    <div className="mt-4 space-y-3">
                      <Slider data-testid="bim3d-area-slider" max={80000} min={0} onValueChange={(value) => setState({ ...state, area: value[0] })} step={100} value={[state.area]} />
                      <div className="flex items-center gap-3">
                        <Input data-testid="bim3d-area-input" id="bim3d-area-input" min={0} onChange={(e) => setState({ ...state, area: Math.max(0, Number(e.target.value) || 0) })} type="number" value={state.area} />
                        <span className="text-sm text-slate-600">m²</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Disciplines bâtiments</Label>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      {buildingDisciplines.map((discipline) => {
                        const active = state.discipline.includes(discipline);
                        const button = (
                          <button
                            className={`relative rounded-xl border px-3 py-2 text-left transition ${active ? "border-[#0f766e] bg-[#0f766e] text-white" : "border-[#d9e7d5] bg-white hover:border-[#99f6e4]"}`}
                            data-testid={`bim3d-discipline-${discipline.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}-button`}
                            key={discipline}
                            onClick={() => toggleDiscipline(discipline)}
                            type="button"
                          >
                            {discipline.replace(" (Data Centers etc...)", "")}
                            {discipline.includes("Electricité Avancé") ? <HelpCircle className="absolute right-2 top-2 h-4 w-4 opacity-80" /> : null}
                          </button>
                        );

                        return discipline.includes("Electricité Avancé") ? (
                          <Tooltip key={discipline}>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent className="max-w-xs text-sm">Systèmes critiques : redondance, monitoring, chemins de câbles haute densité, salles IT/Data Centers, contraintes de disponibilité.</TooltipContent>
                          </Tooltip>
                        ) : (
                          button
                        );
                      })}
                    </div>

                    <div className="mt-6 space-y-3">
                      <Label>Disciplines infrastructures</Label>
                      <div className="rounded-xl border border-[#d9e7d5] bg-white p-4">
                        <div className="flex items-center justify-between text-sm text-slate-600">
                          <span>Surface spécifique partagée</span>
                          <span className="font-medium text-slate-900">{(state.specialSurfaces?.vrd || 0).toLocaleString("fr-FR")} m²</span>
                        </div>
                        <Slider data-testid="bim3d-special-surface-slider" max={80000} min={0} onValueChange={(value) => {
                          const val = Math.max(0, value[0]);
                          setState({ ...state, specialSurfaces: { vrd: val, paysagisme: val, paysagismeElec: val } });
                        }} step={50} value={[state.specialSurfaces?.vrd || 0]} />
                        <div className="mt-3 flex items-center gap-3">
                          <Input data-testid="bim3d-special-surface-input" min={0} onChange={(e) => {
                            const val = Math.max(0, Number(e.target.value) || 0);
                            setState({ ...state, specialSurfaces: { vrd: val, paysagisme: val, paysagismeElec: val } });
                          }} type="number" value={state.specialSurfaces?.vrd || 0} />
                          <span className="text-sm text-slate-600">m²</span>
                        </div>
                      </div>

                      {["VRD", "Paysagisme", "Paysagisme Electricité"].map((discipline) => {
                        const active = state.discipline.includes(discipline);
                        return (
                          <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3" key={discipline}>
                            <span className="font-medium text-slate-900">{discipline}</span>
                            <Button className={active ? "bg-[#0f766e] text-white hover:bg-[#149289]" : ""} data-testid={`bim3d-infra-${discipline.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}-button`} onClick={() => toggleDiscipline(discipline)} type="button" variant={active ? "default" : "outline"}>
                              {active ? "Sélectionné" : "Sélectionner"}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div>
                      <Label>Niveau de détail (LOD)</Label>
                      <RadioGroup className="mt-3 grid grid-cols-3 gap-3" onValueChange={(value) => setState({ ...state, lod: value })} value={String(state.lod)}>
                        {["200", "300", "350/400"].map((level) => (
                          <Label className="flex items-center gap-2 rounded-xl border border-[#d9e7d5] px-3 py-2 cursor-pointer bg-white" data-testid={`bim3d-lod-${level}-option`} key={level}>
                            <RadioGroupItem value={level} /> LOD {level}
                          </Label>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="mt-6">
                      <Label>Quantitatifs et paramétrages</Label>
                      <RadioGroup className="mt-3 grid gap-3" onValueChange={(value) => setState({ ...state, loiPhase: value })} value={state.loiPhase}>
                        {LOI_PHASES.map((phase) => (
                          <Tooltip key={phase.key}>
                            <TooltipTrigger asChild>
                              <Label className="relative flex items-center gap-2 rounded-xl border border-[#d9e7d5] px-3 py-3 cursor-pointer bg-white" data-testid={`bim3d-loi-${phase.key}-option`}>
                                <RadioGroupItem value={phase.label} />
                                <span className="font-medium">{phase.loi}</span>
                                <HelpCircle className="absolute right-2 top-2 h-4 w-4 opacity-80" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-sm">{phase.label} — {phase.tip}</TooltipContent>
                          </Tooltip>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="mt-6 space-y-3">
                      <Label>Options</Label>
                      <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                        <div>
                          <div className="font-medium text-slate-900">Synthèse BIM</div>
                          <div className="text-xs text-slate-600">Comité de synthèse et coordination.</div>
                        </div>
                        <Switch checked={state.synthesis} data-testid="bim3d-synthesis-switch" disabled={!pricingRules.generalSettings?.synthesisOptionEnabled} onCheckedChange={(value) => setState({ ...state, synthesis: value })} />
                      </div>

                      <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                        <div>
                          <div className="font-medium text-slate-900">Sortie de plans</div>
                          <div className="text-xs text-slate-600">Nombre de plans à produire (1 révision incluse).</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button data-testid="bim3d-plans-decrease-button" onClick={() => setState({ ...state, plans: Math.max(0, (state.plans || 0) - 1) })} size="icon" type="button" variant="outline"><Minus className="h-4 w-4" /></Button>
                          <Input className="w-20 text-center" data-testid="bim3d-plans-input" min={0} onChange={(e) => setState({ ...state, plans: Math.max(0, Number(e.target.value) || 0) })} type="number" value={state.plans} />
                          <Button data-testid="bim3d-plans-increase-button" onClick={() => setState({ ...state, plans: (state.plans || 0) + 1 })} size="icon" type="button" variant="outline"><Plus className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Label>Délai</Label>
                      <Select onValueChange={(value) => setState({ ...state, urgency: value })} value={state.urgency}>
                        <SelectTrigger className="mt-3 bg-white" data-testid="bim3d-urgency-trigger"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Standard">Standard</SelectItem>
                          <SelectItem value="Urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Accordion className="w-full" collapsible type="single">
                  <AccordionItem value="client-info">
                    <AccordionTrigger data-testid="bim3d-client-info-trigger">Informations client pour l’aperçu de devis</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-3">
                        <div>
                          <Label htmlFor="bim3d-name-input">Nom</Label>
                          <Input data-testid="bim3d-name-input" id="bim3d-name-input" onChange={(e) => setState({ ...state, name: e.target.value })} value={state.name} />
                        </div>
                        <div>
                          <Label htmlFor="bim3d-company-input">Entreprise</Label>
                          <Input data-testid="bim3d-company-input" id="bim3d-company-input" onChange={(e) => setState({ ...state, company: e.target.value })} value={state.company} />
                        </div>
                        <div>
                          <Label htmlFor="bim3d-email-input">Email</Label>
                          <Input data-testid="bim3d-email-input" id="bim3d-email-input" onChange={(e) => setState({ ...state, email: e.target.value })} type="email" value={state.email} />
                        </div>
                        <div>
                          <Label htmlFor="bim3d-phone-input">Téléphone</Label>
                          <Input data-testid="bim3d-phone-input" id="bim3d-phone-input" onChange={(e) => setState({ ...state, phone: e.target.value })} value={state.phone || ""} />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="bim3d-address-input">Adresse du client</Label>
                          <Input data-testid="bim3d-address-input" id="bim3d-address-input" onChange={(e) => setState({ ...state, clientAddress: e.target.value })} value={state.clientAddress || ""} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_28px_76px_-50px_rgba(15,118,110,0.2)]" data-testid="bim3d-estimate-summary-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-slate-900">Estimation indicative</CardTitle>
                  <span className="rounded-full bg-[#fef2f2] px-3 py-1 text-xs font-semibold text-[#b91c1c]" data-testid="bim3d-estimate-badge">ESTIMATION</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="text-4xl font-semibold text-[#0f766e]" data-testid="bim3d-total-days-value">{days.toFixed(3)} j‑h</div>
                <p className="text-base text-slate-600" data-testid="bim3d-total-price-value">Équivalent : {price.toLocaleString("fr-FR")} € HT</p>
                <div className="space-y-2 text-sm text-slate-600" data-testid="bim3d-estimate-summary-copy">
                  <p>LOD {state.lod} • {LOI_PHASES.find((p) => p.label === state.loiPhase)?.loi}</p>
                  <p>Délai : {state.urgency}</p>
                  <p>Surfaces spéciales : {state.specialSurfaces?.vrd || 0} m²</p>
                </div>
                <div className="flex flex-col gap-3">
                  <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="bim3d-preview-quote-button" onClick={() => setOpenPreview(true)}>
                    Aperçu du devis complet
                  </Button>
                  <Button data-testid="bim3d-download-estimate-button" onClick={() => {
                    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Estimation Bimcubes</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:32px;color:#0f172a}h1{color:#0f766e}table{border-collapse:collapse;width:100%}td,th{border:1px solid #e2e8f0;padding:8px}th{background:#f8fafc;text-align:left}.badge{display:inline-block;padding:2px 8px;border-radius:9999px;background:#fee2e2;color:#b91c1c;font-weight:700;font-size:12px}</style></head><body><h1>Estimation indicative – Bimcubes <span class="badge">ESTIMATION</span></h1><table><tbody><tr><td>Total (jours‑homme)</td><td>${days.toFixed(3)} j‑h</td></tr><tr><td>Équivalent montant</td><td>${price.toLocaleString("fr-FR")} € HT</td></tr></tbody></table></body></html>`;
                    const blob = new Blob([html], { type: "text/html" });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = `estimation-bimcubes-${Date.now()}.html`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                    toast.success("Estimation téléchargée (HTML)");
                  }} variant="outline">
                    <Download className="mr-2 h-4 w-4" /> Télécharger estimation (HTML)
                  </Button>
                </div>
                <p className="text-xs text-[#b91c1c]" data-testid="bim3d-estimate-warning">Important : ceci est une estimation indicative automatisée. Offre non contractuelle.</p>
              </CardContent>
            </Card>
          </div>

          <QuotePreview adminDetail={calcDetail} data={{ state, price, days }} loiPhases={LOI_PHASES} mode="3D" onOpenChange={setOpenPreview} open={openPreview} unitRate={unitRate} />
        </div>
      </section>
    </TooltipProvider>
  );
};